import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fromIso } from '../utils/businessDate';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getTenantToday } from '../utils/dateUtils';
import { fetchClassifications } from './classificationPivot';
import {
  Activity, AlertTriangle, Banknote, Briefcase,
  Database, History, Layers, Lock, Percent, Search, Shield, Ticket, TrendingUp, Unlock, Wallet,
} from 'lucide-react';
import LoanActivityDrawer from './credit/LoanActivityDrawer';
import { CreditActionModal, CreditActionKind, CreditPositionLite } from './credit/CreditLoanActionModals';
import { CORE_CREDIT_LIFECYCLE_ACTIONS } from './credit/CreditLifecycleActions';
import {
  iborPositionPath, leveragedLoanTicketState, loan360Path,
  privateCreditTicketPath, securityMasterPath,
} from '../utils/loanNavigation';
import { AxiomMetricCard, AxiomMetricGrid, AxiomStatusBadge } from './shared';
import {
  CellPrimary, CellSub, InventoryFilters, InventoryFootnote, InventorySearchBox,
  InventoryState, InventoryTable, InventoryTableWrap, InventoryToolbar,
  LifecycleAction, NavAction, RowActionDivider, RowActions,
} from './credit/shared/CreditInventoryKit';

const Container = styled.div<{ $embedded?: boolean }>`
  padding: ${props => (props.$embedded ? '1rem' : '2rem')};
  background: transparent;
  color: #e2e8f0;
`;

const Section = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.34);
  padding: 1.25rem;
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
  color: #f8fafc;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CusipLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: #7dd3fc;
  font-size: 0.75rem;
  font-family: 'JetBrains Mono', monospace;

  &:hover { color: #bae6fd; text-decoration: underline; }
`;

const SecurityLink = styled.button`
  display: block;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f8fafc;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  line-height: 1.35;
  text-align: left;

  &:hover { color: #7dd3fc; text-decoration: underline; }
  &:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; border-radius: 3px; }
`;

const Pnl = styled.span<{ $positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 750;
  color: ${p => (p.$positive ? '#34d399' : '#f87171')};
`;

type PrivateAssetScope = 'all' | 'private-equity' | 'private-credit';

interface RawPosition {
  id?: number;
  securityId?: number;
  securityName?: string;
  cusip?: string;
  isin?: string;
  quantity?: number;
  navPerUnit?: number;
  marketValue?: number;
  costBasis?: number;
  unrealizedPnl?: number;
  lockUpEndDate?: string;
  redemptionWindow?: string;
  reconciliationStatus?: string;
  agentOrAdministrator?: string;
  assetClassName?: string;
  assetSubClassName?: string;
  securityType?: string;
}

interface PrivateAssetPosition {
  id: number;
  securityId: number;
  securityName: string;
  cusip: string;
  isin: string;
  quantity: number;
  navPerUnit: number;
  marketValue: number;
  costBasis: number;
  unrealizedPnl: number;
  lockUpEndDate?: string;
  redemptionWindow?: string;
  reconciliationStatus?: string;
  agentOrAdministrator?: string;
}

interface RawPrivateCreditLoan {
  id?: number;
  loanName?: string;
  borrowerName?: string;
  cusip?: string;
  status?: string;
  securityId?: number;
  security?: {
    id?: number;
    cusip?: string;
    securityName?: string;
  };
  portfolio?: {
    id?: number;
  };
  currentCommitment?: number;
  fundedAmount?: number;
  unfundedAmount?: number;
}

interface PrivateAssetDashboardProps {
  embedded?: boolean;
  scope?: PrivateAssetScope;
}

const scopeCopy: Record<PrivateAssetScope, { title: string; subtitle: string; sectionTitle: string; loading: string }> = {
  all: {
    title: 'Private Assets Dashboard',
    subtitle: 'Monitor NAV trends, liquidity gates, and reconciliation across private market positions.',
    sectionTitle: 'Private Asset Positions',
    loading: 'Loading private assets...',
  },
  'private-equity': {
    title: 'Private Equity Dashboard',
    subtitle: 'Track fund interests, secondaries, lock-up schedules, and valuation marks for private equity sleeves.',
    sectionTitle: 'Private Equity Positions',
    loading: 'Loading private equity positions...',
  },
  'private-credit': {
    title: 'Private Credit Dashboard',
    subtitle: 'Track direct lending, private credit funds, and income sleeves with redemption and reconciliation context.',
    sectionTitle: 'Private Credit Positions',
    loading: 'Loading private credit positions...',
  },
};

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Scope membership comes from the governed classification (canonical L1), not
// from class-name string guesses — those went permanently stale when canonical
// leaf names replaced the legacy asset-class labels (F-HUB-013).
const SCOPE_L1: Record<PrivateAssetScope, string[]> = {
  all: ['Alternatives', 'Private Credit'],
  'private-equity': ['Alternatives'],
  'private-credit': ['Private Credit'],
};

const formatReconciliationStatus = (status?: string) => {
  if (!status) {
    return { label: 'Unavailable', className: 'bg-slate-800 text-slate-400' };
  }

  const normalized = status.toUpperCase();

  if (normalized.includes('MATCH') || normalized.includes('COMPLETE') || normalized.includes('RECONCILED')) {
    return { label: normalized, className: 'bg-emerald-500/10 text-emerald-400' };
  }

  if (normalized.includes('BREAK') || normalized.includes('ERROR') || normalized.includes('FAILED')) {
    return { label: normalized, className: 'bg-red-500/10 text-red-400' };
  }

  if (normalized.includes('PENDING') || normalized.includes('REVIEW')) {
    return { label: normalized, className: 'bg-amber-500/10 text-amber-400' };
  }

  return { label: normalized, className: 'bg-slate-800 text-slate-300' };
};

const PrivateAssetDashboard: React.FC<PrivateAssetDashboardProps> = ({ embedded = false, scope = 'all' }) => {
  const [positions, setPositions] = useState<PrivateAssetPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityRow, setActivityRow] = useState<PrivateAssetPosition | null>(null);
  const [actionCtx, setActionCtx] = useState<{ kind: CreditActionKind; position: CreditPositionLite } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // PIK accrued is a portfolio-scoped, holder-specific balance sourced from the
  // PC/LL risk read-model (fact_pc_ll_analytics_snapshot), keyed by security_id.
  // TODO: wire portfolio-scoped pik_accrued — fetch
  //   GET /api/credit/pc-ll-analytics/risk/portfolio/{portfolioId}?asOfDate=
  //   for the represented portfolio(s) and merge risk rows into this map
  //   ({ securityId -> { pikAccrued, nonAccrual } }). The positions on this
  //   dashboard resolve from fact_positions by security_id and are NOT
  //   portfolio-scoped in this fetch, so the owning portfolio can't be resolved
  //   cleanly yet; the column renders "—" until that scoping (and the backend
  //   pikAccrued field on the risk rows) lands.
  const [pikBySecurityId] = useState<Map<number, { pikAccrued: number; nonAccrual: boolean }>>(() => new Map());

  const copy = scopeCopy[scope];

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const today = getTenantToday();
        const response = await axios.get('/api/ibor/replay', {
          params: { validAsOf: today, knowledgeAsOf: 'now', basis: 'TRADE_DATE' },
        });
        const allPositions: RawPosition[] = Array.isArray(response.data?.positions) ? response.data.positions : [];
        const loanData = scope === 'private-credit'
          ? (await axios.get('/api/private-credit/loans')).data : [];
        const loans: RawPrivateCreditLoan[] = Array.isArray(loanData) ? loanData : [];
        const classIds = Array.from(new Set([
          ...allPositions.map(p => p.securityId),
          ...loans.map(loan => loan.security?.id || loan.securityId),
        ].filter(Boolean).map(Number)));
        const classifications = await fetchClassifications(classIds, today, '').catch(() => new Map());
        const scopeL1s = SCOPE_L1[scope];
        // ABF shares the Private Credit L1 but has its own facility workspace.
        // Apply the same governed L2 scope to holdings and unfunded loan rows.
        const isInScope = (securityId: number) => {
          const classification = classifications.get(securityId);
          return scopeL1s.includes(classification?.l1Name || '')
            && (scope !== 'private-credit' || classification?.l2Name === 'Direct Loan');
        };

        const scopedPositions = allPositions
          .filter((position: RawPosition) =>
            isInScope(Number(position.securityId)))
          .map((position: RawPosition, index: number) => ({
            id: position.id || index + 1,
            securityId: position.securityId || 0,
            securityName: position.securityName || 'Unknown security',
            cusip: position.cusip || '',
            isin: position.isin || '',
            quantity: position.quantity || 0,
            navPerUnit: position.navPerUnit || 0,
            marketValue: position.marketValue || 0,
            costBasis: position.costBasis || 0,
            unrealizedPnl: position.unrealizedPnl || 0,
            lockUpEndDate: position.lockUpEndDate,
            redemptionWindow: position.redemptionWindow,
            reconciliationStatus: position.reconciliationStatus,
            agentOrAdministrator: position.agentOrAdministrator,
          }));

        if (scope === 'private-credit') {
          const visibleSecurityIds = new Set(
            scopedPositions.map((position: PrivateAssetPosition) => position.securityId).filter(Boolean),
          );
          const missingLoanPositions = loans
            .filter((loan: RawPrivateCreditLoan) => {
              const securityId = loan.security?.id || loan.securityId;
              const status = (loan.status || '').toUpperCase();
              return Boolean(securityId)
                && isInScope(Number(securityId))
                && !visibleSecurityIds.has(Number(securityId))
                && !['MATURED', 'REFINANCED', 'DEFAULTED'].includes(status);
            })
            .map((loan: RawPrivateCreditLoan, index: number): PrivateAssetPosition => {
              const securityId = Number(loan.security?.id || loan.securityId || 0);
              const fundedAmount = toNumber(loan.fundedAmount);
              return {
                id: -1 * (loan.id || index + 1),
                securityId,
                securityName: loan.security?.securityName || loan.loanName || loan.borrowerName || 'Private credit loan',
                cusip: loan.security?.cusip || loan.cusip || '',
                isin: '',
                quantity: fundedAmount,
                navPerUnit: 0,
                marketValue: fundedAmount,
                costBasis: fundedAmount,
                unrealizedPnl: 0,
                lockUpEndDate: undefined,
                redemptionWindow: undefined,
                reconciliationStatus: undefined,
                agentOrAdministrator: undefined,
              };
            });
          setPositions([...scopedPositions, ...missingLoanPositions]);
          // TODO: wire portfolio-scoped pik_accrued — once these rows carry the
          // owning portfolio, fetch the PC/LL risk read-model per represented
          // portfolio and populate pikBySecurityId (see the map declaration).
        } else {
          setPositions(scopedPositions);
        }
      } catch (error) {
        console.error('Error loading private asset dashboard data:', error);
        setPositions([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [scope, refreshKey]);

  const summary = useMemo(() => {
    const totalValue = positions.reduce((sum, position) => sum + position.marketValue, 0);
    const totalPnl = positions.reduce((sum, position) => sum + position.unrealizedPnl, 0);
    const lockedUpCount = positions.filter(
      position => position.lockUpEndDate && new Date(position.lockUpEndDate) > fromIso(getTenantToday()),
    ).length;
    const reconciledCount = positions.filter(position => {
      const status = formatReconciliationStatus(position.reconciliationStatus);
      return status.className.includes('emerald');
    }).length;

    return {
      totalValue,
      totalPnl,
      positionCount: positions.length,
      lockedUpCount,
      redeemableCount: Math.max(positions.length - lockedUpCount, 0),
      reconciledCount,
    };
  }, [positions]);

  // Resolve the PC loan (silo PK + economics + borrower) for a position's
  // security_id, then open the requested lifecycle modal. The dashboard reads
  // fact_positions and only knows security_id; the modals need the loan PK.
  const openAction = useCallback(async (kind: CreditActionKind, row: PrivateAssetPosition) => {
    try {
      const { data } = await axios.get(`/api/private-credit/by-security/${row.securityId}`);
      if (!data?.found) {
        window.alert('No private credit loan is registered for this position (e.g. a PC fund interest).');
        return;
      }
      const num = (v: any) => (v == null ? undefined : Number(v));
      const position: CreditPositionLite = {
        loanRecordId: data.loanRecordId,
        securityId: row.securityId,
        loanName: data.loanName || row.securityName,
        borrowerName: data.borrowerName,
        fundedAmount: num(data.fundedAmount),
        currentCommitment: num(data.currentCommitment),
        unfundedAmount: num(data.unfundedAmount),
        reborrowable: Boolean(data.reborrowable),
        spreadBps: num(data.spreadBps),
        allInRate: num(data.allInRate),
        baseRate: data.baseRate,
        floorRate: num(data.floorRate),
        csaBps: num(data.csaBps),
        currentIndexFixingPct: num(data.currentIndexFixingPct),
        maturityDate: data.maturityDate,
        status: data.status,
        facilityType: data.facilityType,
        ddtlCommitment: num(data.ddtlCommitment),
        ddtlFunded: num(data.ddtlFunded),
        ddtlUndrawn: num(data.ddtlUndrawn),
        ddtlExpiry: data.ddtlExpiry,
      };
      setActionCtx({ kind, position });
    } catch (e) {
      window.alert('Failed to load loan details for this position.');
    }
  }, []);

  // Loan 360 is a loan-record view. The dashboard only knows security_id, so
  // resolve the PC loan PK on click (same lookup the lifecycle actions use),
  // then deep-link. Fund interests have no loan record — say so plainly.
  const openLoan360 = useCallback(async (row: PrivateAssetPosition) => {
    try {
      const { data } = await axios.get(`/api/private-credit/by-security/${row.securityId}`);
      if (data?.found && data.loanRecordId) {
        navigate(loan360Path('PRIVATE_CREDIT', data.loanRecordId));
      } else {
        window.alert('No private credit loan is registered for this position (e.g. a PC fund interest).');
      }
    } catch (e) {
      window.alert('Failed to open Loan 360 for this position.');
    }
  }, [navigate]);

  const visiblePositions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return positions;
    return positions.filter(position => [
      position.securityName,
      position.cusip,
      position.agentOrAdministrator,
    ].some(value => String(value || '').toLowerCase().includes(term)));
  }, [positions, searchTerm]);

  // PC "PIK Accrued" cell: money-formatted accrued PIK (or "—" when 0/absent),
  // plus an amber non-accrual badge for matched rows. Reads pikBySecurityId,
  // which is empty until portfolio-scoped pik_accrued is wired (see the TODO on
  // the map declaration) — so today this renders "—" without breaking the row.
  const renderPikAccrued = (position: PrivateAssetPosition) => {
    const pik = pikBySecurityId.get(position.securityId);
    const pikAccrued = pik?.pikAccrued ?? 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>{pikAccrued > 0 ? `$${pikAccrued.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</span>
        {pik?.nonAccrual && <AxiomStatusBadge $tone="amber">Non-Accrual</AxiomStatusBadge>}
      </div>
    );
  };

  if (loading) {
    return (
      <Container $embedded={embedded}>
        <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500/20 border-t-blue-500" />
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{copy.loading}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container $embedded={embedded}>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-white">
            <Lock className="h-6 w-6 text-blue-400" />
            {copy.title}
          </h1>
          <p className="text-sm font-medium text-slate-500">{copy.subtitle}</p>
        </div>
        <AxiomStatusBadge $tone="cyan">
          <Activity className="h-3 w-3" />
          Position Monitoring
        </AxiomStatusBadge>
      </div>

      <AxiomMetricGrid style={{ marginBottom: '1.25rem' }}>
        <AxiomMetricCard
          tone="cyan"
          icon={<Wallet />}
          label="Total Value"
          value={`$${summary.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
        />
        <AxiomMetricCard
          tone={summary.totalPnl >= 0 ? 'emerald' : 'red'}
          icon={<TrendingUp />}
          label="Unrealized P&L"
          value={(
            <span style={{ color: summary.totalPnl >= 0 ? '#34d399' : '#f87171' }}>
              ${summary.totalPnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          )}
        />
        <AxiomMetricCard tone="slate" icon={<Layers />} label="Positions" value={summary.positionCount} />
        <AxiomMetricCard tone="amber" icon={<Lock />} label="Locked" value={summary.lockedUpCount} />
        <AxiomMetricCard tone="blue" icon={<Unlock />} label="Redeemable" value={summary.redeemableCount} />
      </AxiomMetricGrid>

      <Section>
        <SectionTitle>
          <Briefcase className="h-4 w-4 text-blue-400" />
          {copy.sectionTitle}
        </SectionTitle>

        <InventoryToolbar style={{ marginBottom: '0.85rem' }}>
          <InventoryFilters>
            <InventorySearchBox>
              <Search size={15} />
              <input
                placeholder="Search security, CUSIP, agent / fund admin…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InventorySearchBox>
          </InventoryFilters>
        </InventoryToolbar>

        {visiblePositions.length === 0 ? (
          <InventoryState>No positions match the current search.</InventoryState>
        ) : (
          <InventoryTableWrap>
            <InventoryTable style={{ minWidth: scope === 'private-credit' ? 1300 : 920 }}>
              <thead>
                <tr>
                  <th>Security</th>
                  <th>CUSIP</th>
                  <th>Units</th>
                  <th>NAV / Unit</th>
                  <th>Market Value</th>
                  <th>Unrealized P&L</th>
                  {scope === 'private-credit' && <th title="Portfolio-scoped accrued PIK from the PC/LL risk read-model">PIK Accrued</th>}
                  <th>Lock-Up Ends</th>
                  <th>Agent / Fund Admin</th>
                  {scope === 'private-credit' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visiblePositions.map(position => (
                  <tr key={position.id}>
                    <td>
                      {scope === 'private-credit' && position.securityId ? <SecurityLink onClick={() => openLoan360(position)} title="Open Loan 360">
                        {position.securityName}
                      </SecurityLink> : <CellPrimary>{position.securityName}</CellPrimary>}
                    </td>
                    <td>
                      {position.cusip ? (
                        <CusipLink
                          onClick={() => navigate(`/securities?cusip=${encodeURIComponent(position.cusip)}`)}
                          title="Open in Security Master"
                        >
                          {position.cusip}
                        </CusipLink>
                      ) : (
                        <CellSub>—</CellSub>
                      )}
                    </td>
                    <td>{position.quantity ? position.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0'}</td>
                    <td>{position.navPerUnit ? `$${position.navPerUnit.toFixed(4)}` : 'N/A'}</td>
                    <td>{position.marketValue ? `$${position.marketValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}</td>
                    <td>
                      <Pnl $positive={position.unrealizedPnl >= 0}>
                        {position.unrealizedPnl >= 0 ? '↑' : '↓'} ${Math.abs(position.unrealizedPnl).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </Pnl>
                    </td>
                    {scope === 'private-credit' && <td>{renderPikAccrued(position)}</td>}
                    <td>{position.lockUpEndDate ? new Date(position.lockUpEndDate).toLocaleDateString() : 'N/A'}</td>
                    <td>{position.agentOrAdministrator || <CellSub>—</CellSub>}</td>
                    {scope === 'private-credit' && (
                      <td>
                        {position.securityId ? (
                          <RowActions>
                            {/* Common actions (shared with LL) — same set + order */}
                            {position.cusip && (
                              <>
                                <NavAction tip="Security Master" onClick={() => navigate(securityMasterPath(position.cusip))}>
                                  <Database size={15} />
                                </NavAction>
                                <NavAction
                                  tip="Populate trade ticket"
                                  onClick={() => navigate(
                                    privateCreditTicketPath(position.cusip),
                                    leveragedLoanTicketState({
                                      securityId: position.securityId,
                                      cusip: position.cusip,
                                      securityName: position.securityName,
                                    }),
                                  )}
                                >
                                  <Ticket size={15} />
                                </NavAction>
                                <NavAction tip="IBOR exposure" onClick={() => navigate(iborPositionPath(position.cusip))}>
                                  <Briefcase size={15} />
                                </NavAction>
                              </>
                            )}
                            <NavAction tip="Activity timeline" onClick={() => setActivityRow(position)}>
                              <History size={15} />
                            </NavAction>
                            <RowActionDivider />
                            {CORE_CREDIT_LIFECYCLE_ACTIONS.map(action => (
                              <LifecycleAction
                                key={action.kind}
                                tip={action.title}
                                color={action.color}
                                onClick={() => openAction(action.kind, position)}
                              >
                                <action.Icon size={15} />
                              </LifecycleAction>
                            ))}
                            {/* PC-specific */}
                            <LifecycleAction tip="Writedown" color="#fbbf24" onClick={() => openAction('writedown', position)}>
                              <AlertTriangle size={15} />
                            </LifecycleAction>
                            <LifecycleAction tip="Payoff" color="#34d399" onClick={() => openAction('payoff', position)}>
                              <Banknote size={15} />
                            </LifecycleAction>
                            <LifecycleAction tip="Capitalize PIK" color="#c4b5fd" onClick={() => openAction('pc-pik-capitalize', position)}>
                              <Layers size={15} />
                            </LifecycleAction>
                            <LifecycleAction tip="PIK election" color="#c4b5fd" onClick={() => openAction('pc-pik-election', position)}>
                              <Percent size={15} />
                            </LifecycleAction>
                          </RowActions>
                        ) : (
                          <CellSub>—</CellSub>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </InventoryTable>
          </InventoryTableWrap>
        )}
        <InventoryFootnote>Showing {visiblePositions.length} of {positions.length} positions</InventoryFootnote>
      </Section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section>
          <SectionTitle>
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            Valuation Oversight
          </SectionTitle>
          <div className="flex h-48 flex-col items-center justify-center text-slate-600">
            <Activity className="mb-4 h-12 w-12 opacity-20" />
            <p className="text-sm italic">Historical valuation analytics will appear here as marks are ingested.</p>
          </div>
        </Section>

        <Section>
          <SectionTitle>
            <Shield className="h-4 w-4 text-blue-400" />
            Reconciliation Health
          </SectionTitle>
          <div className="flex h-48 flex-col items-center justify-center text-slate-600">
            <Shield className="mb-4 h-12 w-12 opacity-20" />
            <p className="text-sm italic">
              {summary.reconciledCount > 0
                ? `${summary.reconciledCount} positions are currently reconciled. Review the grid for exceptions.`
                : 'No reconciled positions have been reported yet for this sleeve.'}
            </p>
          </div>
        </Section>
      </div>

      {activityRow && (
        <LoanActivityDrawer
          kind="PC"
          securityId={activityRow.securityId}
          loanName={activityRow.securityName}
          onClose={() => setActivityRow(null)}
        />
      )}

      {actionCtx && (
        <CreditActionModal
          kind={actionCtx.kind}
          assetKind="PC"
          position={actionCtx.position}
          onClose={() => setActionCtx(null)}
          onSuccess={() => { setActionCtx(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </Container>
  );
};

export default PrivateAssetDashboard;
